import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { colors } from "../theme/colors";
import { Grid, Loader, Paper } from "@mantine/core";
import { useProgramAvailabilitiesQuery } from "../hooks/react-query/useConsultAvailabilitiesQuery";
import { useParams } from "react-router-dom";
import { removeDuplicateDates } from "../utils/helpers";
import AppointmentDateSelect from "../components/AppointmentDateSelect";
import AppointmentTimeSelect from "../components/AppointmentTimeSelect";
import EditTimezone from "../components/EditTimezone";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useStorage } from "../context/StorageContext";
import { FHPage } from "../utils/pageUtils";
import { useLatestConsultRealtime } from "../hooks/react-query/useLatestConsultRealtime";
import { useToast } from "../context/ToastContext";
import { NavContext } from "@ionic/react";
import { ConsultType } from "../typings/enums";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getUserProfile } from "../services/firestoreService";
import { usePostHog } from "posthog-js/react";
// Analytics events for reschedule flow tracking
import {
  APPOINTMENT_SLOTS_LOAD_STARTED,
  APPOINTMENT_SLOTS_LOAD_FAILED,
  APPOINTMENT_SLOTS_EMPTY,
  APPOINTMENT_SLOTS_LOAD_TIMEOUT,
} from "../constants/posthogEvents";
import { trackOnce } from "../utils/tracking";
import "./AppointmentScheduler.css";

dayjs.extend(utc);
dayjs.extend(timezone);

interface Params {
  index: string;
  selectedDate?: string;
}

type Props = {
  selectDateProps?: {
    pageTitle: string;
    title: string;
    description: string;
  };
  selectTimeProps?: {
    pageTitle: string;
  };
  cancelPreviousConsult?: boolean;
};

const DEFAULT_SELECT_DATE_PROPS = {
  pageTitle: "SCHEDULE",
  title: "Schedule Your Appointment",
  description: "",
};

const DEFAULT_SELECT_TIME_PROPS = {
  pageTitle: "TIMEZONE",
};

const AppointmentScheduler = ({
  selectDateProps = DEFAULT_SELECT_DATE_PROPS,
  selectTimeProps = DEFAULT_SELECT_TIME_PROPS,
  cancelPreviousConsult,
}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { index, selectedDate } = useParams<Params>();
  const { isStorageReady, getItem } = useStorage();
  const [defaultTz, setDefaultTz] = useState<string>(dayjs.tz.guess());
  const posthog = usePostHog();
  const loadStartTimeRef = useRef<number | null>(null);
  const loadStartedTrackedRef = useRef(false);
  const timeoutTrackedRef = useRef(false);
  const emptyTrackedRef = useRef(false);
  const failedTrackedRef = useRef(false);

  useEffect(() => {
    if (isStorageReady) {
      getItem("defaultTz").then((val) => {
        if (val) {
          setDefaultTz(val as string);
        }
      });
    }
  });

  const { addToast } = useToast();
  const navContext = useContext(NavContext);
  const { currentUser } = useAuth();

  const { data: userProfile } = useQuery({
    queryKey: ["userProfile", currentUser?.uid],
    queryFn: () => getUserProfile(currentUser!),
    enabled: !!currentUser,
  });

  const { data: consultForAvailability } = useLatestConsultRealtime(
    [ConsultType.scheduled_video_visit, ConsultType.followup_video_visit],
    true
  );

  const {
    data: programData,
    isLoading: availabilityLoading,
    isError,
    error,
  } = useProgramAvailabilitiesQuery(
    consultForAvailability?.usState as string,
    consultForAvailability?.consultType as string,
    userProfile?.isAsync ?? false
  );

  // Track load started when query is enabled and starts loading
  useEffect(() => {
    const shouldTrackLoadStarted =
      consultForAvailability?.usState &&
      consultForAvailability?.consultType &&
      !loadStartedTrackedRef.current;

    if (shouldTrackLoadStarted && availabilityLoading) {
      loadStartTimeRef.current = Date.now();
      loadStartedTrackedRef.current = true;
      timeoutTrackedRef.current = false;
      void trackOnce(posthog, APPOINTMENT_SLOTS_LOAD_STARTED, {
        flow_type: cancelPreviousConsult ? "reschedule" : "schedule",
        usState: consultForAvailability.usState,
        consultType: consultForAvailability.consultType,
        isAsync: userProfile?.isAsync ?? false,
      });
    }
  }, [
    availabilityLoading,
    consultForAvailability?.usState,
    consultForAvailability?.consultType,
    userProfile?.isAsync,
    posthog,
    cancelPreviousConsult,
  ]);

  // Track load failed (use capture so event fires every time; ref prevents duplicate in same load cycle)
  useEffect(() => {
    if (isError && !failedTrackedRef.current) {
      failedTrackedRef.current = true;
      posthog?.capture(APPOINTMENT_SLOTS_LOAD_FAILED, {
        flow_type: cancelPreviousConsult ? "reschedule" : "schedule",
        error:
          error instanceof Error
            ? error.name || (error as { code?: string }).code || "UnknownError"
            : String(error),
        usState: consultForAvailability?.usState,
        consultType: consultForAvailability?.consultType,
      });
    }
  }, [isError, error, posthog, cancelPreviousConsult, consultForAvailability]);

  // Track empty slots (use capture so event fires every time; ref prevents duplicate in same load cycle)
  useEffect(() => {
    if (
      !availabilityLoading &&
      programData &&
      programData.availability &&
      programData.availability.length === 0 &&
      !emptyTrackedRef.current
    ) {
      emptyTrackedRef.current = true;
      posthog?.capture(APPOINTMENT_SLOTS_EMPTY, {
        flow_type: cancelPreviousConsult ? "reschedule" : "schedule",
        usState: consultForAvailability?.usState,
        consultType: consultForAvailability?.consultType,
        isAsync: userProfile?.isAsync ?? false,
      });
    }
  }, [
    availabilityLoading,
    programData,
    posthog,
    cancelPreviousConsult,
    consultForAvailability,
    userProfile,
  ]);

  // Reset refs when loading state changes (new load starts or finishes)
  useEffect(() => {
    if (availabilityLoading) {
      // New load started - reset tracking refs so new load cycle can emit events
      // Note: loadStartedTrackedRef is NOT reset here to prevent duplicate events
      // if consultForAvailability or userProfile change during loading
      loadStartTimeRef.current = Date.now();
      timeoutTrackedRef.current = false;
      emptyTrackedRef.current = false;
      failedTrackedRef.current = false;
    } else {
      // Loading finished - reset all refs so next load cycle can be tracked
      loadStartTimeRef.current = null;
      loadStartedTrackedRef.current = false;
      timeoutTrackedRef.current = false;
    }
  }, [availabilityLoading]);

  // Track timeout (8 seconds after load started)
  useEffect(() => {
    if (
      loadStartTimeRef.current &&
      availabilityLoading &&
      !timeoutTrackedRef.current
    ) {
      const timeoutId = setTimeout(() => {
        if (availabilityLoading && loadStartTimeRef.current) {
          timeoutTrackedRef.current = true;
          posthog?.capture(APPOINTMENT_SLOTS_LOAD_TIMEOUT, {
            flow_type: cancelPreviousConsult ? "reschedule" : "schedule",
            elapsed_ms: Date.now() - loadStartTimeRef.current,
            usState: consultForAvailability?.usState,
            consultType: consultForAvailability?.consultType,
          });
        }
      }, 8000); // 8 seconds

      return () => clearTimeout(timeoutId);
    }
  }, [
    availabilityLoading,
    posthog,
    cancelPreviousConsult,
    consultForAvailability,
  ]);

  const availabilityDates = useMemo(() => {
    if (!programData) return [];
    const dates: dayjs.Dayjs[] = programData.availability.map(
      (dateStr: string) => {
        return dayjs.tz(dateStr, defaultTz).startOf("D");
      }
    );

    return removeDuplicateDates(dates);
  }, [programData, defaultTz]);

  const handleSuccess = () => {
    navContext.navigate("/tabs/home");
    addToast(
      cancelPreviousConsult
        ? "Appointment Rescheduled"
        : "Appointment Scheduled",
      "success"
    );
  };

  const renderCurrentScreen = () => {
    if (availabilityLoading || isLoading) {
      return (
        <Paper radius="md">
          <Grid justify="center">
            <Loader size="lg" mt="xl" color={colors.blurple}></Loader>
          </Grid>
        </Paper>
      );
    }
    switch (index) {
      case "0":
        return (
          <AppointmentDateSelect
            availabilityDates={availabilityDates}
            consultId={consultForAvailability?.guid ?? ""}
            defaultTz={defaultTz}
            title={selectDateProps.title}
            description={selectDateProps.description}
          />
        );
      case "1":
        return (
          <AppointmentTimeSelect
            currentTz={defaultTz}
            selectedDate={selectedDate ?? ""}
            availability={programData?.availability}
            consult={consultForAvailability}
            setIsLoading={setIsLoading}
            onSuccess={handleSuccess}
            cancelPreviousConsult={cancelPreviousConsult}
          />
        );
      case "2":
        return <EditTimezone currentTimezone={defaultTz} />;
    }
  };

  return (
    <FHPage
      title={
        index === "2" ? selectTimeProps.pageTitle : selectDateProps.pageTitle
      }
      withBackground={false}
    >
      {renderCurrentScreen()}
    </FHPage>
  );
};

export default AppointmentScheduler;
